/**
 * "Delete Application" confirm (V2-GAPS-AND-PDF-PLAN §D). Posts
 * `intent=deleteApplication` — a recoverable SOFT delete (`deletedAt`) that
 * also soft-deletes a now-orphaned guarantor server-side. Superadmin or
 * `applications.delete` only. On success the record is gone, so we navigate
 * back to the property's applications list rather than staying on a deleted
 * page.
 */
import { ArrowRight, Trash2 } from 'lucide-react'
import { useFetcher, useNavigate } from 'react-router'
import type {
	ApplicationSummary,
	DetailActionResult,
} from '#app/utils/application-detail-v2/types'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from './dialog-bits'

export function DeleteApplicationDialog({
	open,
	onOpenChange,
	application,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const navigate = useNavigate()
	const busy = fetcher.state !== 'idle'

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Application deleted"
				onSuccess={() => {
					onOpenChange(false)
					void navigate(
						`/properties/${application.propertyId ?? ''}/applications`,
					)
				}}
			/>
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title="Delete Application"
				description={`This removes ${application.applicantName}'s application from your lists. It can be restored by an administrator if needed. A guarantor left with no applicant is removed too.`}
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<InlineActionError fetcher={fetcher} open={open} />
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="dark"
						disabled={busy}
						onClick={() => {
							void fetcher.submit(
								{ intent: 'deleteApplication', applicationId: application.id },
								{ method: 'post' },
							)
						}}
					>
						{busy ? 'Deleting...' : 'Delete Application'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
